/**
 * ApAgingTab — Antigüedad de saldos CxP.
 */
import { DataTable } from '@/components/ds/DataTable'
import { EmptyState } from '@/components/EmptyState'
import { Download, TrendingDown, CheckCircle2 } from 'lucide-react'
import { useApAging, useDpo } from '../hooks/useFinance'
import { useTenantLocale, formatNumber } from '@/lib/locale'
import DownloadButton from '@/components/DownloadButton'
import { AGING_BUCKETS, agingBucket } from '@/components/ds/agingBuckets'

export default function ApAgingTab() {
  const { data, isLoading } = useApAging()
  const { data: dpoData } = useDpo(6)
  const locale = useTenantLocale()
  const fmt = (value: string | number) => {
    const n = typeof value === 'string' ? parseFloat(value) : value
    const decimals = n % 1 !== 0 ? 2 : 0
    return `$${formatNumber(n, locale, decimals)}`
  }

  const summary = data?.summary
  const suppliers = data?.by_supplier || []

  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        {summary && AGING_BUCKETS.map(({ key, label }) => {
          return (
            <div key={key} className="bg-glass backdrop-blur-glass border border-divider rounded-card p-4 shadow-card">
              <p className="text-xs text-content-muted font-medium">{label}</p>
              <p className="text-lg font-bold text-content mt-1">{fmt(summary[key] || '0')}</p>
            </div>
          )
        })}
        <div className="bg-danger-surface border border-danger-border rounded-xl p-4">
          <p className="text-xs text-danger-content-soft font-medium">Total CxP</p>
          <p className="text-lg font-bold text-danger-content mt-1">{fmt(summary?.total || '0')}</p>
        </div>
      </div>

      {dpoData && (
        <div className="flex items-center gap-3 mb-4 p-3 bg-glass backdrop-blur-glass border border-divider rounded-card shadow-card">
          <TrendingDown size={18} className="text-danger-solid" />
          <span className="text-sm text-content-muted">
            {dpoData.current_dpo ? (
              <>
                DPO <strong>{dpoData.current_dpo} días</strong>
                {dpoData.current_period && (
                  <span className="text-content-subtle"> · {dpoData.current_period}</span>
                )}
              </>
            ) : (
              /* Sin compras en el período no hay DPO. Mostrar 0 días diría
                 que se paga al contado, que es lo contrario. */
              <>DPO <strong>sin dato</strong>{' '}
                <span className="text-content-subtle">— sin compras suficientes en los meses cerrados para un valor representativo</span></>
            )}
          </span>
          <DownloadButton path="/finance/payables/aging/export/" filename="aging_cxp.csv"
            label="Exportar CSV" iconSize={12}
            className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border border-divider rounded-lg hover:bg-soft" />
        </div>
      )}

      <DataTable
        columns={[
          {
            key: 'proveedor', header: 'Proveedor', sortable: true,
            sortValue: (s: any) => s.supplier_name,
            render: (s: any) => <span className="font-medium text-content">{s.supplier_name}</span>,
          },
          {
            key: 'rut', header: 'RUT', sortable: true,
            sortValue: (s: any) => s.supplier_tax_id,
            render: (s: any) => <span className="font-mono">{s.supplier_tax_id}</span>,
          },
            {
              key: 'current', header: agingBucket('current').short, numeric: true, sortable: true,
              sortValue: (s: any) => Number(s['current']),
              render: (s: any) => fmt(s['current']),
            },
            {
              key: '1_30', header: agingBucket('1_30').short, numeric: true, sortable: true,
              sortValue: (s: any) => Number(s['1_30']),
              render: (s: any) => fmt(s['1_30']),
            },
            {
              key: '31_60', header: agingBucket('31_60').short, numeric: true, sortable: true,
              sortValue: (s: any) => Number(s['31_60']),
              render: (s: any) => fmt(s['31_60']),
            },
            {
              key: '61_90', header: agingBucket('61_90').short, numeric: true, sortable: true,
              sortValue: (s: any) => Number(s['61_90']),
              render: (s: any) => fmt(s['61_90']),
            },
          {
            key: 'over_90', header: agingBucket('over_90').short, numeric: true, sortable: true,
            sortValue: (s: any) => Number(s.over_90),
            render: (s: any) => (
              <span className="font-medium text-danger-content-soft">{fmt(s.over_90)}</span>
            ),
          },
          {
            key: 'total', header: 'Total', numeric: true, sortable: true,
            sortValue: (s: any) => Number(s.total),
            render: (s: any) => <span className="font-bold">{fmt(s.total)}</span>,
          },
        ]}
        rows={suppliers}
        rowKey={(s: any) => s.supplier_id}
        loading={isLoading}
        empty={<EmptyState icon={CheckCircle2} size="sm" title="Sin saldos pendientes." />}
        minWidth={900}
        caption="Antigüedad de saldos por pagar"
      />
    </>
  )
}
